import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField } from '@mui/material';
import VendorTopMenuBar from './VendorLayouts/VendorTopMenuBar';
import VendorSideMenuBar from './VendorLayouts/VendorSideMenuBar';
import { useCart } from '../../CartProvider';
import createLogger from '../../Utils/debugLogger';

const logger = createLogger('Vendor/PrinterSettings');

const getKey = (vendorId) => `printerSettings:${vendorId || 'unknown'}`;

export default function PrinterSettings() {
    const { currentVendorId } = useCart();
    const [enableAutoPrint, setEnableAutoPrint] = useState(true);
    const [backendUrl, setBackendUrl] = useState('');
    const [connectionType, setConnectionType] = useState('bluetooth'); // bluetooth | usb | network
    const [ip, setIp] = useState('');
    const [port, setPort] = useState('9100');
    const [btName, setBtName] = useState('');
    const [btMac, setBtMac] = useState('');
    const PRINTING_API = process.env.REACT_APP_PRINTING_BACKEND_URL;

    const storageKey = useMemo(() => getKey(currentVendorId), [currentVendorId]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const cfg = JSON.parse(raw);
                setEnableAutoPrint(!!cfg.enableAutoPrint);
                setConnectionType(cfg.connectionType || 'bluetooth');
                setIp(cfg.ip || '');
                setPort(String(cfg.port || '9100'));
                setBtName(cfg.btName || '');
                setBtMac(cfg.btMac || '');
                setBackendUrl(cfg.backendUrl || '');
            }
        } catch (e) {
            logger.warn('Failed to load printer settings', { message: e?.message });
        }
        // Default backend URL if not set: env var or current host:8080
        setBackendUrl((prev) => prev || PRINTING_API || `http://${window.location.hostname}:8080`);
    }, [storageKey]);

    const save = () => {
        try {
            const cfg = { enableAutoPrint, connectionType, ip, port: parseInt(port, 10) || 9100, btName, btMac, backendUrl };
            localStorage.setItem(storageKey, JSON.stringify(cfg));
            logger.info('Printer settings saved', { cfg });
            alert('Printer settings saved');
        } catch (e) {
            logger.warn('Save failed', { message: e?.message });
        }
    };

    const effectiveBackend = () => (backendUrl || PRINTING_API || `http://${window.location.hostname}:8080`).replace(/\/$/, '');

    const testConnection = async () => {
        const base = effectiveBackend();
        try {
            const res = await fetch(`${base}/health`, { method: 'GET' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await res.json();
            alert(`Connected to printing backend at ${base}`);
        } catch (e) {
            logger.warn('Health check failed', { message: e?.message, base });
            alert(`Cannot reach printing backend at ${base}. Check URL, network, and that the service is running.`);
        }
    };

    const testPrint = async () => {
        const base = effectiveBackend();
        try {
            const orderId = `test-${currentVendorId || 'vendor'}`;
            const payload = {
                restaurantId: currentVendorId,
                items: [
                    { name: 'Test Item', qty: 1, price: 0.0 },
                ],
                total: 0.0,
                timestamp: new Date().toISOString(),
                printer: {
                    enableAutoPrint,
                    connectionType,
                    ip,
                    port: parseInt(port, 10) || 9100,
                    btName,
                    btMac,
                },
                meta: { type: 'printerTest' },
            };
            logger.info('Calling printer backend for test', { endpoint: `${base}/api/orders/${orderId}/accept`, payload });
            await fetch(`${base}/api/orders/${orderId}/accept`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            alert('Test print triggered. Check the tablet/printer.');
        } catch (e) {
            logger.warn('Test print failed', { message: e?.message });
            alert('Test print failed. See console for details.');
        }
    };

    const rawbtTest = async () => {
        try {
            const text = [
                '      OH MY FOOD',
                '   --------------------',
                '   Test receipt from portal',
                `   Time: ${new Date().toLocaleString()}`,
                '   --------------------',
                '   Thank you for choosing',
                '        Oh My Food!',
            ].join('\n');
            const payload = encodeURIComponent(text);
            const open = (url) => {
                try { window.location.href = url; }
                catch (err) { logger.warn('RawBT URL open failed', { message: err?.message }); }
            };
            // Attempt RawBT direct + SEND fallback + Share
            open(`intent://#Intent;package=ru.a402d.rawbtprinter;action=ru.a402d.rawbtprinter.action.PRINT;S.text=${payload};end`);
            setTimeout(() => open(`intent://#Intent;action=android.intent.action.SEND;type=text/plain;S.android.intent.extra.TEXT=${payload};package=ru.a402d.rawbtprinter;end`), 600);
            setTimeout(async () => {
                if (navigator.share) {
                    try { await navigator.share({ text }); }
                    catch (err) { logger.warn('Web Share print failed', { message: err?.message }); }
                }
            }, 1200);
        } catch (e) {
            logger.warn('RawBT test failed', { message: e?.message });
        }
    };

    return (
        <div className='profile-settings-home-vendor'>
            <VendorSideMenuBar />
            <VendorTopMenuBar />
            <div className='vendorsettings-cont'>
                <div className='editprofile-main' style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <h2>Printer Settings</h2>
                    <FormControlLabel
                        control={<Switch checked={enableAutoPrint} onChange={(e) => setEnableAutoPrint(e.target.checked)} />}
                        label='Enable Auto Print on Accept'
                    />

                    <TextField
                        label='Printing Backend URL'
                        placeholder='http://192.168.1.10:8080'
                        value={backendUrl}
                        onChange={(e) => setBackendUrl(e.target.value)}
                        helperText='Use your LAN IP if testing from another device. Default is current host:8080.'
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel id='conn-type-label'>Connection Type</InputLabel>
                        <Select
                            labelId='conn-type-label'
                            value={connectionType}
                            label='Connection Type'
                            onChange={(e) => setConnectionType(e.target.value)}
                        >
                            <MenuItem value='usb'>USB</MenuItem>
                            <MenuItem value='network'>Network (IP)</MenuItem>
                            <MenuItem value='bluetooth'>Bluetooth</MenuItem>
                        </Select>
                    </FormControl>

                    {connectionType === 'network' && (
                        <div style={{ display: 'grid', gap: 12 }}>
                            <TextField label='Printer IP Address' placeholder='192.168.1.100' value={ip} onChange={(e) => setIp(e.target.value)} />
                            <TextField label='Port' placeholder='9100' value={port} onChange={(e) => setPort(e.target.value)} />
                        </div>
                    )}

                    {connectionType === 'bluetooth' && (
                        <div style={{ display: 'grid', gap: 12 }}>
                            <TextField label='Bluetooth Device Name' placeholder='Printer-80mm' value={btName} onChange={(e) => setBtName(e.target.value)} />
                            <TextField label='Bluetooth MAC (optional)' placeholder='00:11:22:33:44:55' value={btMac} onChange={(e) => setBtMac(e.target.value)} />
                        </div>
                    )}

                    {connectionType === 'usb' && (
                        <div style={{ color: '#6b7280' }}>
                            USB printers are auto-detected by the tablet’s printer app.
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                        <Button variant='contained' onClick={save}>Save Settings</Button>
                        <Button variant='outlined' onClick={testConnection}>Test Connection</Button>
                        <Button variant='outlined' onClick={testPrint}>Print Test (Backend)</Button>
                        <Button variant='outlined' onClick={rawbtTest}>Print Now (RawBT)</Button>
                    </div>

                    <div style={{ color: '#6b7280', marginTop: 8 }}>
                        Tips:
                        <ul>
                            <li>Network: Find the printer’s IP from its test page. Default port is 9100.</li>
                            <li>Bluetooth: Put printer in pairing mode, pair on tablet, then enter name/MAC.</li>
                            <li>USB: Most thermal printers are auto-detected by RawBT or the printer app.</li>
                            <li>No backend? Leave the Backend URL empty. On Android, the portal will try to open RawBT directly after Accept.</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
